// How the storefront script reads its own <script> tag.
//
// The install is one tag:
//   <script src="https://popups.flowapt.com/widget/popup-widget.umd.js"
//           data-organization-id="…"></script>
// so the tag alone must be enough: an organisation id starts the widget and,
// without a popup id, shows the org's live (active-on-store) popup. Older
// installs (the Shopify app embed, the pre-29 Oct snippet) still send
// data-auto-init / data-active-on-store / data-popup-id and keep their meaning.

export const CANONICAL_SERVICE_BASE = "https://flowapt-popup-service.vercel.app";

/** The service the script talks to = the host it was loaded from, when that
 *  host is ours: popups.flowapt.com (or any *.flowapt.com), the vercel.app
 *  name, or one of its preview deployments. Anything else (a merchant's CDN
 *  copy, localhost, a page that inlined the bundle, no tag at all) keeps the
 *  canonical host, which is exactly the old behaviour. */
export function serviceBaseFromScriptSrc(src: string | null | undefined): string {
  if (!src) return CANONICAL_SERVICE_BASE;
  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return CANONICAL_SERVICE_BASE;
  }
  if (url.protocol !== "https:") return CANONICAL_SERVICE_BASE;
  const host = url.hostname.toLowerCase();
  const ours =
    host === "flowapt.com" ||
    host.endsWith(".flowapt.com") ||
    (host.startsWith("flowapt-popup-service") && host.endsWith(".vercel.app"));
  return ours ? url.origin : CANONICAL_SERVICE_BASE;
}

// Captured while the bundle evaluates: `document.currentScript` is only set
// during that first synchronous run (null in callbacks and in ES-module loads).
const loadedFrom =
  typeof document !== "undefined"
    ? (document.currentScript as HTMLScriptElement | null)?.src
    : undefined;

export const SERVICE_BASE = serviceBaseFromScriptSrc(loadedFrom);

export interface EmbedOptions {
  /** Start the widget on its own when the bundle loads. */
  start: boolean;
  organizationId?: string;
  popupId?: string;
  /** Ask for the org's live popup (popupId, when given, is the fallback). */
  activeOnStore: boolean;
  debug: boolean;
  previewMode?: string;
}

const flag = (v: string | undefined) => v?.trim().toLowerCase();

export function embedOptionsFromDataset(
  data: DOMStringMap | Record<string, string | undefined>
): EmbedOptions {
  const organizationId = data.organizationId?.trim() || undefined;
  const popupId = data.popupId?.trim() || undefined;

  // data-auto-init="false" keeps a manual install manual. Any other
  // data-auto-init starts it (a tag with no org id then logs why it can't).
  // Without data-auto-init, an organisation id is enough. The app's own
  // previews inject the bundle with no data-* and call init themselves.
  const autoInit = flag(data.autoInit);
  const start = autoInit !== undefined ? autoInit !== "false" : !!organizationId;

  // Explicit true / false wins; unset = the live popup unless a popup id
  // names one.
  const active = flag(data.activeOnStore);
  const activeOnStore = active === "true" ? true : active === "false" ? false : !popupId;

  return {
    start,
    organizationId,
    popupId,
    activeOnStore,
    debug: flag(data.debug) === "true",
    previewMode: data.previewMode?.trim() || undefined,
  };
}
